import React, { useEffect, useState } from 'react'

import { useBottomScrollListener } from 'react-bottom-scroll-listener'
import { useTranslation } from 'react-i18next'

import ControlArea from '@components/myScores/ControlArea'
import RemoveFavoriteButton from '@components/myScores/ControlArea/RemoveFavoriteButton'
import ScoresView from '@components/myScores/ScoresView'
import { ScoreViewOptions } from '@components/myScores/utils/types'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import NonIdealState from '@components/shared/NonIdealState'
import { FAVORITES_PAGE_SIZE, FavoriteScoresResponse } from '@pages/home/loaders/favorites.loader'
import SelectionProvider from '@providers/SelectionProvider'
import ApiRoutes from '@utils/api-routes'
import { ViewType } from '@utils/global'
import http from '@utils/http'
import Storage from '@utils/storage'

interface Props {
  initialData: FavoriteScoresResponse
  order: string | null
}

const FavoritesView: React.FC<Props> = ({ initialData, order }) => {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(false)
  const [scoresData, setScoresData] = useState(initialData)
  const [layoutType, setLayoutType] = useState(Storage.scoresViewType)
  const setViewType = (type: ViewType): void => {
    setLayoutType(type)
    Storage.scoresViewType = type
  }

  const { scores, remaining, startIndex } = scoresData

  const scrollRef = useBottomScrollListener<HTMLDivElement>(
    async () => {
      if (!remaining || loading) return

      setLoading(true)
      const [newResp] = await http.post<FavoriteScoresResponse>(
        ApiRoutes.members.favoriteScoresPath(),
        {
          order,
          max_count: FAVORITES_PAGE_SIZE,
          start_index: startIndex,
        }
      )

      if (newResp?.data) {
        setScoresData((prevData) => ({
          ...newResp.data,
          scores: [...prevData.scores, ...newResp.data.scores],
        }))
      }
      setLoading(false)
    },
    {
      triggerOnNoScroll: true,
      offset: 32,
    }
  )

  // Reset the data when we get something new loaded
  useEffect(() => {
    setScoresData(initialData)
  }, [initialData])

  if (!scores.length) {
    return (
      <NonIdealState
        title={t('content.empty.favorites.title')}
        icon="favorite-outline"
        subtitle={t('content.empty.favorites.subtitle')}
      />
    )
  }

  const options: ScoreViewOptions = {
    showAuthor: true,
    hideState: true,
  }

  return (
    <SelectionProvider initialItems={scores}>
      <ControlArea setViewType={setViewType} viewType={layoutType} order={order}>
        <RemoveFavoriteButton />
      </ControlArea>
      <div className="flex-1 overflow-y-auto relative pb-4" ref={scrollRef}>
        <ScoresView scores={scores} viewType={layoutType} options={options} />
        {remaining && loading && <LoadingSpinner />}
      </div>
    </SelectionProvider>
  )
}

export default FavoritesView
